import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {CopyableMarkdownBlock} from './CopyableMarkdownBlock';
import {PromptModalityChoice} from '../../prompts/PromptModalityChoice';

const MODE_MINIMAL = 'minimal';
const MODE_FULL = 'full';

/**
 * Paso "pegá esto en las instrucciones de tu Project", con las dos variantes que devuelve el
 * Starter Pack.
 *
 * Muchas agencias llegan acá con el Project ya armado y con instrucciones propias que les
 * gustan. Hasta ahora la pantalla ofrecía un solo bloque —el pack completo, que dicta
 * personalidad, formato y extensión— sin decir en ningún lado qué hacer con lo que ya estaba:
 * o lo pisaban o abandonaban el paso. El selector nombra ese caso y le da la variante corta,
 * que es ADITIVA (se pega al final, no reemplaza nada).
 */
export function ProjectInstructionsBlock({
    minimalMarkdown, fullMarkdown, minimalPrompt, instructionsPrompt, copyStatus, onCopied, clientId,
}) {
    const {t} = useTranslation();
    // Default MINIMAL: pegar el bloque corto nunca destruye lo que el Project ya tenga, el pack
    // completo sí puede. Ante la duda, la opción que no rompe nada del usuario.
    const [mode, setMode] = useState(MODE_MINIMAL);
    const [mergeOpen, setMergeOpen] = useState(false);

    // Metadatos del prompt oficial detrás del texto activo, para el ping de copiado y el chip
    // de versión. Con un backend viejo (sin `minimal_prompt`/`instructions_prompt`) quedan
    // null y el bloque degrada a solo-texto.
    const promptMetaFor = (prompt) => (prompt && prompt.id
        ? {id: prompt.id, version: prompt.version, agencyClientId: clientId}
        : null);
    const statusFor = (prompt) => (prompt && prompt.id && copyStatus ? copyStatus[prompt.id] : null);

    // Backend viejo (sin `minimal_markdown`): el selector no tiene qué ofrecer, así que
    // la pantalla se comporta como antes en vez de mostrar una opción vacía.
    if (!minimalMarkdown) {
        return (
            <CopyableMarkdownBlock
                title={t('ia_assistant.instructions_title')}
                description={t('ia_assistant.instructions_desc')}
                markdown={fullMarkdown}
                promptMeta={promptMetaFor(instructionsPrompt)}
                versionStatus={statusFor(instructionsPrompt)}
                onCopied={onCopied}
            />
        );
    }

    const isMinimal = mode === MODE_MINIMAL;
    const activePrompt = isMinimal ? minimalPrompt : instructionsPrompt;

    const option = (value, key) => ({
        value,
        title: t(`ia_assistant.picker_${key}`),
        description: t(`ia_assistant.picker_${key}_desc`),
    });

    return (
        <div className="ia-instructions">
            <PromptModalityChoice
                name="ia-instructions-mode"
                question={t('ia_assistant.picker_question')}
                options={[
                    option(MODE_MINIMAL, 'keep_existing'),
                    option(MODE_FULL, 'new_project'),
                ]}
                value={mode}
                onChange={setMode}
            />

            <CopyableMarkdownBlock
                title={t('ia_assistant.instructions_title')}
                description={t(isMinimal
                    ? 'ia_assistant.instructions_desc_minimal'
                    : 'ia_assistant.instructions_desc_full')}
                markdown={isMinimal ? minimalMarkdown : fullMarkdown}
                promptMeta={promptMetaFor(activePrompt)}
                versionStatus={statusFor(activePrompt)}
                onCopied={onCopied}
            />

            {isMinimal && (
                <div className="ia-instructions-merge">
                    <button
                        type="button"
                        className="ia-instructions-merge-toggle"
                        aria-expanded={mergeOpen}
                        onClick={() => setMergeOpen(!mergeOpen)}
                    >
                        <FaIcon iconName={mergeOpen ? 'chevron-down' : 'chevron-right'}/>
                        {t('ia_assistant.merge_title')}
                    </button>
                    {mergeOpen && (
                        <div className="ia-instructions-merge-body">
                            <p className="ia-instructions-merge-help">{t('ia_assistant.merge_help')}</p>
                            <CopyableMarkdownBlock
                                title={t('ia_assistant.merge_block_title')}
                                markdown={buildMergePrompt(t, minimalMarkdown)}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Prompt para pegar en un chat DENTRO del Project: es el propio agente el que fusiona, con las
 * instrucciones actuales ya en su contexto. Así el texto del usuario nunca sale de su cuenta
 * (no lo recibimos ni lo guardamos) y el resultado conserva su voz.
 *
 * `escapeValue: false` es obligatorio acá: el bloque interpolado tiene «», barras y comillas,
 * y el default de i18next las convertiría en entidades HTML (gotcha conocido del repo).
 */
function buildMergePrompt(t, minimalMarkdown) {
    return t('ia_assistant.merge_prompt', {
        block: minimalMarkdown,
        interpolation: {escapeValue: false},
    });
}
